/**
 * Project Insights Page
 * 
 * Search past IOD projects to find relevant examples.
 * Searches across overview, description, themes/sectors, and technical categories.
 * Returns ranked results with associated team allocations.
 */

import React, { useState } from 'react';
import {
  Box, Paper, Typography, Grid, CircularProgress, Alert,
  TextField, InputAdornment, Button, Chip, Divider,
  Card, CardContent, Collapse, IconButton, Tooltip,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  LinearProgress, Stack
} from '@mui/material';
import {
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Person as PersonIcon,
  Category as CategoryIcon,
  Description as DescriptionIcon,
  TrendingUp as TrendingUpIcon,
  Groups as GroupsIcon,
  CheckCircle as CheckCircleIcon,
  HelpOutline as HelpOutlineIcon
} from '@mui/icons-material';
import axios from 'axios';

const API_BASE = process.env.REACT_APP_API_URL || '/api';

interface TermHits {
  technical_categories: number;
  themes_sectors: number;
  overview: number;
  description: number;
  total: number;
}

interface Allocation {
  id: number;
  person_name: string;
  person_type: 'iod_staff' | 'associate' | 'partner';
  role: string;
  fee_per_day: number | null;
  rca_rate: number | null;
  compass_name: string | null;
  compass_associate_id: number | null;
}

interface ProjectResult {
  id: number;
  project_number: string;
  project_name: string;
  overview: string;
  client: string;
  staff_owner: string;
  project_status: string;
  themes_sectors: string;
  technical_categories: string;
  description: string;
  created_at: string;
  updated_at: string;
  termHits: Record<string, TermHits>;
  baseScore: number;
  finalScore: number;
  allocations: Allocation[];
}

interface SearchResponse {
  success: boolean;
  query: string;
  interpretation: string;
  searchTerms: string[];
  primaryTerms: string[];
  expandedTerms: string[];
  resultCount: number;
  results: ProjectResult[];
}

const ProjectInsightsPage: React.FC = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [results, setResults] = useState<ProjectResult[]>([]);
  const [searchTerms, setSearchTerms] = useState<string[]>([]);
  const [interpretation, setInterpretation] = useState<string>('');
  const [primaryTerms, setPrimaryTerms] = useState<string[]>([]);
  const [expandedTerms, setExpandedTerms] = useState<string[]>([]);
  const [expandedProjects, setExpandedProjects] = useState<Set<string>>(new Set());

  const handleSearch = async () => {
    if (!searchQuery.trim()) return;

    setIsLoading(true);
    setError(null);

    try {
      const response = await axios.post<SearchResponse>(`${API_BASE}/project-insights`, {
        query: searchQuery,
        limit: 10
      });

      if (response.data.success) {
        setResults(response.data.results);
        setSearchTerms(response.data.searchTerms);
        setInterpretation(response.data.interpretation || '');
        setPrimaryTerms(response.data.primaryTerms || []);
        setExpandedTerms(response.data.expandedTerms || []);
        // Auto-expand first result
        if (response.data.results.length > 0) {
          setExpandedProjects(new Set([response.data.results[0].project_number]));
        }
      } else {
        setError('Search failed');
      }
    } catch (err: any) {
      setError(err.response?.data?.error || err.message || 'Search failed');
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const toggleExpanded = (projectNumber: string) => {
    const newExpanded = new Set(expandedProjects);
    if (newExpanded.has(projectNumber)) {
      newExpanded.delete(projectNumber);
    } else {
      newExpanded.add(projectNumber);
    }
    setExpandedProjects(newExpanded);
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'info' | 'default' => {
    const s = status?.toLowerCase() || '';
    if (s.includes('completed')) return 'success';
    if (s.includes('active')) return 'info';
    if (s.includes('framework')) return 'warning';
    return 'default';
  };

  const getPersonTypeColor = (type: string): 'primary' | 'secondary' | 'warning' => {
    switch (type) {
      case 'iod_staff': return 'primary';
      case 'associate': return 'secondary';
      case 'partner': return 'warning';
      default: return 'primary';
    }
  };

  const getPersonTypeLabel = (type: string): string => {
    switch (type) {
      case 'iod_staff': return 'IOD Staff';
      case 'associate': return 'Associate';
      case 'partner': return 'Partner';
      default: return type;
    }
  };

  // Calculate relevance percentage relative to top result
  const getRelevancePercent = (score: number): number => {
    if (results.length === 0) return 0;
    const maxScore = results[0].finalScore;
    if (maxScore === 0) return 0;
    return Math.round((score / maxScore) * 100);
  };

  const getRelevanceLabel = (percent: number): string => {
    if (percent >= 90) return 'Excellent';
    if (percent >= 70) return 'Strong';
    if (percent >= 50) return 'Good';
    if (percent >= 30) return 'Moderate';
    return 'Partial';
  };

  const getRelevanceColor = (percent: number): string => {
    if (percent >= 90) return '#22B8A7';
    if (percent >= 70) return '#4CAF50';
    if (percent >= 50) return '#8BC34A';
    if (percent >= 30) return '#FFC107';
    return '#FF9800';
  };

  const highlightTerms = (text: string, terms: string[]): React.ReactNode => {
    if (!text || terms.length === 0) return text;
    
    let result = text;
    terms.forEach(term => {
      const regex = new RegExp(`(${term.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
      result = result.replace(regex, '**$1**');
    });
    
    // Convert **term** to styled spans
    const parts = result.split(/\*\*([^*]+)\*\*/g);
    return parts.map((part, i) => 
      i % 2 === 1 ? (
        <Box component="span" key={i} sx={{ 
          backgroundColor: 'rgba(34, 184, 167, 0.3)', 
          color: '#22B8A7',
          fontWeight: 600,
          px: 0.5,
          borderRadius: 0.5
        }}>
          {part}
        </Box>
      ) : part
    );
  };

  const truncateText = (text: string, maxLength: number = 300): string => {
    if (!text || text.length <= maxLength) return text || '';
    return text.substring(0, maxLength) + '...';
  };

  return (
    <Box sx={{ p: 3, width: '100%' }}>
      {/* Header */}
      <Paper sx={{ p: 3, mb: 3, background: 'linear-gradient(135deg, #14161A 0%, #1B1E23 100%)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
          <Typography variant="h5" sx={{ fontWeight: 600, color: '#22B8A7' }}>
            Project Insights
          </Typography>
          <Tooltip 
            title={
              <Box sx={{ p: 1 }}>
                <Typography variant="subtitle2" sx={{ fontWeight: 600, mb: 1 }}>How Scoring Works</Typography>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  Projects are ranked by keyword matches across:
                </Typography>
                <Typography variant="body2">• Technical Categories: 35%</Typography>
                <Typography variant="body2">• Themes/Sectors: 30%</Typography>
                <Typography variant="body2">• Overview: 20%</Typography>
                <Typography variant="body2">• Description: 15%</Typography>
                <Typography variant="body2" sx={{ mt: 1, fontStyle: 'italic' }}>
                  Recent projects (last 3 years) get a 10% bonus.
                </Typography>
              </Box>
            }
            arrow
            placement="right"
          >
            <IconButton size="small" sx={{ color: 'text.secondary' }}>
              <HelpOutlineIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Search Input */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <TextField
            fullWidth
            placeholder="e.g., gender women girls, climate adaptation, food security..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            onKeyPress={handleKeyPress}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon sx={{ color: 'text.secondary' }} />
                </InputAdornment>
              ),
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                backgroundColor: '#1B1E23',
              }
            }}
          />
          <Button
            variant="contained"
            onClick={handleSearch}
            disabled={isLoading || !searchQuery.trim()}
            sx={{ minWidth: 120 }}
          >
            {isLoading ? <CircularProgress size={24} /> : 'Search'}
          </Button>
        </Box>

        {/* AI Interpretation */}
        {interpretation && (
          <Box sx={{ mt: 2, p: 1.5, backgroundColor: 'rgba(34, 184, 167, 0.1)', borderRadius: 1, border: '1px solid rgba(34, 184, 167, 0.2)' }}>
            <Typography variant="body2" sx={{ color: '#22B8A7', fontStyle: 'italic' }}>
              {interpretation}
            </Typography>
          </Box>
        )}

        {/* Search Terms Display */}
        {searchTerms.length > 0 && (
          <Box sx={{ mt: 2 }}>
            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center', mb: 1 }}>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontWeight: 500 }}>
                Primary:
              </Typography>
              {primaryTerms.map((term, i) => (
                <Chip
                  key={i}
                  label={term}
                  size="small"
                  sx={{ 
                    backgroundColor: 'rgba(34, 184, 167, 0.25)',
                    color: '#22B8A7',
                    fontWeight: 600
                  }}
                />
              ))}
            </Box>
            {expandedTerms.length > 0 && (
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  + AI expanded:
                </Typography>
                {expandedTerms.map((term, i) => (
                  <Chip
                    key={i}
                    label={term}
                    size="small"
                    variant="outlined"
                    sx={{ 
                      borderColor: 'rgba(34, 184, 167, 0.3)',
                      color: 'rgba(34, 184, 167, 0.8)',
                      fontSize: '0.75rem'
                    }}
                  />
                ))}
              </Box>
            )}
          </Box>
        )}
      </Paper>

      {/* Error Display */}
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {/* Loading */}
      {isLoading && (
        <Box sx={{ mb: 3 }}>
          <LinearProgress />
        </Box>
      )}

      {/* Results */}
      {results.length > 0 && (
        <Box>
          <Typography variant="h6" sx={{ mb: 2, color: 'text.secondary' }}>
            Top {results.length} Matching Projects
          </Typography>

          <Stack spacing={2}>
            {results.map((project, index) => (
              <Card 
                key={project.project_number}
                sx={{ 
                  backgroundColor: '#14161A',
                  border: '1px solid rgba(255,255,255,0.12)',
                  '&:hover': {
                    borderColor: 'rgba(34, 184, 167, 0.3)'
                  }
                }}
              >
                <CardContent sx={{ pb: 1 }}>
                  {/* Project Header */}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                    <Box sx={{ flex: 1 }}>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1, flexWrap: 'wrap' }}>
                        <Typography variant="h6" sx={{ fontWeight: 600, color: '#22B8A7' }}>
                          #{index + 1} {project.project_number} | {project.client || 'Unknown Client'} | {project.project_name || 'Untitled Project'}
                        </Typography>
                        <Chip 
                          label={project.project_status} 
                          size="small" 
                          color={getStatusColor(project.project_status)}
                          variant="outlined"
                        />
                        <Tooltip 
                          title={
                            <Box sx={{ p: 0.5 }}>
                              <Typography variant="body2" sx={{ fontWeight: 600 }}>
                                {getRelevancePercent(project.finalScore)}% Match ({getRelevanceLabel(getRelevancePercent(project.finalScore))})
                              </Typography>
                              <Typography variant="caption" sx={{ display: 'block', mt: 0.5 }}>
                                Based on keyword matches across categories, themes, overview & description.
                                {index === 0 ? ' This is the best match.' : ` Compared to top result.`}
                              </Typography>
                            </Box>
                          }
                          arrow
                        >
                          <Chip
                            icon={<TrendingUpIcon />}
                            label={`${getRelevancePercent(project.finalScore)}% Match`}
                            size="small"
                            sx={{ 
                              backgroundColor: `${getRelevanceColor(getRelevancePercent(project.finalScore))}20`,
                              color: getRelevanceColor(getRelevancePercent(project.finalScore)),
                              fontWeight: 600
                            }}
                          />
                        </Tooltip>
                      </Box>
                      
                      {/* Staff Owner */}
                      {project.staff_owner && (
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 1 }}>
                          <PersonIcon fontSize="small" sx={{ color: 'text.secondary' }} />
                          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                            {project.staff_owner}
                          </Typography>
                        </Box>
                      )}
                    </Box>

                    <IconButton onClick={() => toggleExpanded(project.project_number)}>
                      {expandedProjects.has(project.project_number) ? <ExpandLessIcon /> : <ExpandMoreIcon />}
                    </IconButton>
                  </Box>

                  {/* Overview Preview */}
                  {project.overview && (
                    <Box sx={{ mb: 2 }}>
                      <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.6 }}>
                        {highlightTerms(truncateText(project.overview, 250), searchTerms)}
                      </Typography>
                    </Box>
                  )}

                  {/* Categories & Themes Chips */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 1 }}>
                    {project.technical_categories && (
                      <Chip
                        icon={<CategoryIcon sx={{ color: '#22B8A7 !important' }} />}
                        label={project.technical_categories}
                        size="small"
                        sx={{ 
                          backgroundColor: 'rgba(34, 184, 167, 0.15)',
                          color: '#22B8A7',
                          border: '1px solid rgba(34, 184, 167, 0.3)',
                          maxWidth: 'none',
                          height: 'auto',
                          '& .MuiChip-label': {
                            whiteSpace: 'normal',
                            padding: '8px 4px'
                          }
                        }}
                      />
                    )}
                    {project.themes_sectors && (
                      <Chip
                        icon={<DescriptionIcon sx={{ color: '#22B8A7 !important' }} />}
                        label={project.themes_sectors}
                        size="small"
                        sx={{ 
                          backgroundColor: 'rgba(34, 184, 167, 0.15)',
                          color: '#22B8A7',
                          border: '1px solid rgba(34, 184, 167, 0.3)',
                          maxWidth: 'none',
                          height: 'auto',
                          '& .MuiChip-label': {
                            whiteSpace: 'normal',
                            padding: '8px 4px'
                          }
                        }}
                      />
                    )}
                  </Box>

                  {/* Expanded Content */}
                  <Collapse in={expandedProjects.has(project.project_number)}>
                    <Divider sx={{ my: 2 }} />

                    {/* Full Overview */}
                    {project.overview && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#22B8A7' }}>
                          Full Overview
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.7 }}>
                          {highlightTerms(project.overview, searchTerms)}
                        </Typography>
                      </Box>
                    )}

                    {/* Description */}
                    {project.description && (
                      <Box sx={{ mb: 3 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#22B8A7' }}>
                          Description
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.7 }}>
                          {highlightTerms(project.description, searchTerms)}
                        </Typography>
                      </Box>
                    )}

                    {/* Term Hits Breakdown */}
                    <Box sx={{ mb: 3 }}>
                      <Typography variant="subtitle2" sx={{ mb: 1, color: '#22B8A7' }}>
                        Search Term Matches
                      </Typography>
                      <Grid container spacing={1}>
                        {Object.entries(project.termHits).map(([term, hits]) => (
                          <Grid item xs={12} sm={6} md={4} key={term}>
                            <Paper sx={{ p: 1.5, backgroundColor: '#1B1E23' }}>
                              <Typography variant="body2" sx={{ fontWeight: 600, color: '#22B8A7', mb: 0.5 }}>
                                "{term}"
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                                {hits.technical_categories > 0 && (
                                  <Chip label={`Tech: ${hits.technical_categories}`} size="small" />
                                )}
                                {hits.themes_sectors > 0 && (
                                  <Chip label={`Theme: ${hits.themes_sectors}`} size="small" />
                                )}
                                {hits.overview > 0 && (
                                  <Chip label={`Overview: ${hits.overview}`} size="small" />
                                )}
                                {hits.description > 0 && (
                                  <Chip label={`Desc: ${hits.description}`} size="small" />
                                )}
                              </Box>
                            </Paper>
                          </Grid>
                        ))}
                      </Grid>
                    </Box>

                    {/* Team Allocations */}
                    {project.allocations && project.allocations.length > 0 && (
                      <Box>
                        <Typography variant="subtitle2" sx={{ mb: 1, color: '#22B8A7', display: 'flex', alignItems: 'center', gap: 1 }}>
                          <GroupsIcon fontSize="small" />
                          Team Allocations ({project.allocations.length})
                        </Typography>
                        <TableContainer component={Paper} sx={{ backgroundColor: '#1B1E23' }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell>Name</TableCell>
                                <TableCell>Type</TableCell>
                                <TableCell>Role</TableCell>
                                <TableCell align="right">Day Rate</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {project.allocations.map((alloc) => (
                                <TableRow key={alloc.id}>
                                  <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                      {alloc.compass_associate_id && (
                                        <Tooltip title="Linked to Compass">
                                          <CheckCircleIcon fontSize="small" sx={{ color: '#22B8A7' }} />
                                        </Tooltip>
                                      )}
                                      {alloc.compass_name || alloc.person_name}
                                    </Box>
                                  </TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={getPersonTypeLabel(alloc.person_type)} 
                                      size="small"
                                      color={getPersonTypeColor(alloc.person_type)}
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell>{alloc.role || '-'}</TableCell>
                                  <TableCell align="right">
                                    {alloc.fee_per_day ? `£${alloc.fee_per_day.toLocaleString()}` : '-'}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}

                    {project.allocations && project.allocations.length === 0 && (
                      <Typography variant="body2" sx={{ color: 'text.secondary', fontStyle: 'italic' }}>
                        No team allocations found for this project.
                      </Typography>
                    )}
                  </Collapse>
                </CardContent>
              </Card>
            ))}
          </Stack>
        </Box>
      )}

      {/* No Results */}
      {!isLoading && results.length === 0 && searchTerms.length > 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#14161A' }}>
          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
            No matching projects found
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Try different keywords or broader search terms.
          </Typography>
        </Paper>
      )}

      {/* Initial State */}
      {!isLoading && results.length === 0 && searchTerms.length === 0 && (
        <Paper sx={{ p: 4, textAlign: 'center', backgroundColor: '#14161A' }}>
          <SearchIcon sx={{ fontSize: 48, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" sx={{ color: 'text.secondary', mb: 1 }}>
            Search for Past Projects
          </Typography>
          <Typography variant="body2" sx={{ color: 'text.secondary' }}>
            Enter keywords to find relevant project examples from IOD's portfolio.
          </Typography>
        </Paper>
      )}
    </Box>
  );
};

export default ProjectInsightsPage;
